import GameObject from '../../../GameObject/GameObject';
import AnimationClip from '../AnimationClip';
import BaseCard from '../../../Prototype/Level/Card/BaseCard';
import TransformComponent from '../../../Component/Transform';
import { Easing } from '@tweenjs/tween.js';
import {
    AwaitIterators,
    GetAnimationClip,
    PlayAnimation,
} from '../AnimationHelper';
import Renderable from '../../../Component/Renderable';
import CardComponent from '../../../Component/Level/Card';
import KeyCard from '../../../Prototype/Level/Card/KeyCard';
import { delay } from '../../../Utility/Task/TaskQueue';
import Point from '../../../Utility/Point';
import CardSystem from '../../Card/CardSystem';
import { showCardScoreEffect } from '../CardAnimationHelper';
import { UIEvent } from '../../UI/BaseUISystem';
import { DragonbonesDefault } from './DragonbonesDefault';
import SpriteComponent from 'Component/Render/Sprite';
import { DragonbonesFadeIn } from './DragonbonesFadeIn';
import Armature from 'Prototype/Armature';

interface ICardKeyUnlockMeta {
    system: CardSystem;
    sourceCard: KeyCard;
    targetCards: BaseCard[];
}

export class CardKeyUnlock extends AnimationClip {
    public *execute(
        animator: GameObject,
        meta: ICardKeyUnlockMeta
    ): IterableIterator<number> {
        const animsToPlay = [];

        // Move key card to animator.
        animator.addChild(meta.sourceCard);

        // Move lock cards to animator.
        for (const lockCard of meta.targetCards) {
            animator.addChild(lockCard);
        }

        yield* delay(100);

        const sourceCardTween = this.getTween(meta.sourceCard.getComponent(TransformComponent));
        yield* this.startTween(
            sourceCardTween
                .to({ rotation: 0 }, 300)
                .easing(Easing.Quadratic.Out)
        );

        // Flip cards over if not already.
        const lockCardFlips = [];
        for (const lockCard of meta.targetCards) {
            if (lockCard.getComponent(CardComponent).faceUp === false) {
                lockCard.getComponent(CardComponent).faceUp = true;
                lockCardFlips.push(
                    PlayAnimation(
                        GetAnimationClip('CardFlip', lockCard, { faceUp: true })
                    )
                );
            }
        }
        yield* AwaitIterators(lockCardFlips);
        yield* delay(100);

        // Hide source key card.
        meta.sourceCard.getComponent(Renderable).visible = false;

        const keyUnlockDuration = 600;

        const keyCards = [];
        const keyCardsAnims = [];
        for (const lockCard of meta.targetCards) {
            // Create new key cards to animate.
            const keyCard: KeyCard = GameObject.Instantiate(KeyCard);
            animator.addChild(keyCard);
            keyCards.push(keyCard);

            const lockCardTransform = lockCard.getComponent(TransformComponent);
            const keyCardTransform = keyCard.getComponent(TransformComponent);

            keyCardTransform.position.copy(
                meta.sourceCard.getComponent(TransformComponent)
                    .position as Point
            );

            const tweenPosition = this.getTween(keyCardTransform.position);
            tweenPosition
                .delay(600)
                .to(
                    {
                        x: lockCardTransform.position.x,
                        y: lockCardTransform.position.y,
                    },
                    keyUnlockDuration
                )
                .easing(Easing.Quadratic.Out);

            const cardArmature = keyCard.getArmature();
            cardArmature.getComponent(Renderable).alpha = 1;
            keyCard
                .getChildByName(BaseCard.CARD_FRONT_NAME)
                .getComponent(SpriteComponent).visible = false;
            keyCard
                .getChildByName(BaseCard.CARD_BACK_NAME)
                .getComponent(SpriteComponent).visible = false;

            keyCardsAnims.push(
                PlayAnimation(
                    new DragonbonesDefault(cardArmature, {
                        playTimes: 1,
                    }))
            );

            keyCardsAnims.push(this.startTween(tweenPosition));

            for (const lockCard of meta.targetCards) {
                const lockArmature = lockCard.getArmature();
                keyCardsAnims.push(
                    PlayAnimation(
                        new DragonbonesFadeIn(lockArmature, {
                            playTimes: 1,
                            animationName: 'key_impact'
                        })
                    )
                );
            }
            // PlaySound('hazard-key-use');
        }

        const alphaTween = this.getTween(meta.sourceCard.getComponent(Renderable));
        alphaTween.to({ alpha: 0 }, 100).start(this.elapsedTime);

        yield* AwaitIterators(keyCardsAnims);

        for (const lockCard of meta.targetCards) {
            lockCard
                .getChildByName(BaseCard.CARD_FRONT_NAME)
                .getComponent(SpriteComponent).visible = false;
        }

        // Destroy key cards.
        for (const keyCard of keyCards) {
            keyCard.destroy();
        }

        showCardScoreEffect(meta.system.scene, meta.sourceCard);

        meta.system.scene.emit('UpdateScoreProgress');

        for (const lockCard of meta.targetCards) {
            const lockArmature = lockCard.getChildByName('cardArmature');
            animsToPlay.push(
                PlayAnimation(
                    new DragonbonesDefault(lockArmature, {
                        playTimes: 1,
                        animationName: 'unlock'
                    })
                )
            );
        }

        yield* AwaitIterators(animsToPlay);

        // PlaySound('hazard-lock-open');

        // Fade out lock cards
        for (const lockCard of meta.targetCards) {
            const alphaTween = this.getTween(lockCard.getComponent(Renderable));
            alphaTween.to({ alpha: 0 }, 100).start(this.elapsedTime);

            showCardScoreEffect(meta.system.scene, lockCard);
        }

        yield* delay(1000);
        meta.system.scene.emit(UIEvent.RESTORE_INTERACTIVITY, true);
    }
}
